import { useState } from "react";
import { accountService } from "@miplatform/api";
import { HTTP_STATUSES } from "@miplatform/constants";
import { useComponentState } from "@miplatform/hooks";
import { toastService } from "@miplatform/toast";
import { useGlobalManagerApproval } from "@mibuildweb/components";

export const useReconcileDocumentsSectionViewModel = ({
  onDelete,
  onRefresh,
}) => {
  const stateManager = useComponentState();
  const isLoading = stateManager.isBusy();
  const { executeApiRequest } = useGlobalManagerApproval();
  const [backcharges, setBackcharges] = useState([]);

  const deletePurchaseOrder = ({ id, modifiedOn, name }) => {
    stateManager.run(async () => {
      const response = await accountService.billingCommitments.delete({
        id,
        modifiedOn,
        toastMessageConfig: {
          success: {
            text: `Payment information ${name} was deleted successfully.`,
          },
        },
      });
      if (response.status === HTTP_STATUSES.OK) {
        onDelete();
      }
    });
  };

  const voidPurchaseOrder = ({ id, modifiedOn }) =>
    stateManager.run(async () => {
      const options = await accountService.billingCommitments.voidOptions({
        id,
        modifiedOn,
      });

      const response = await executeApiRequest({
        options,
        callback: onRefresh,
        toastMessageForManagerApproval: {
          text: `Voiding an order requires manager approval.`,
          subtext: `Please provide your username and PIN to proceed.`,
        },
      });

      if (response.status === HTTP_STATUSES.NO_CONTENT) {
        toastService.success({
          text: "Payment information voided successfully.",
        });
        return response.data;
      }

      return null;
    });

  const createBackcharge = (data) =>
    stateManager.run(async () => {
      const response = await accountService.creditMemos.create({
        data,
        toastMessageConfig: {
          success: { text: "Backcharge created successfully." },
        },
      });
      return response;
    });

  const getBackcharges = ({ homeId, accountId }) =>
    stateManager.run(async () => {
      const response = await accountService.creditMemos.getAll({
        filters: {
          homeId: {
            eq: { type: "guid", value: homeId },
          },
          accountId: {
            eq: { type: "guid", value: accountId },
          },
          voidDate: {
            eq: null,
          },
        },
      });
      if (response.status === HTTP_STATUSES.OK) {
        setBackcharges(response.data || []);
      }
    });

  const updateBackcharge = ({ id, data, modifiedOn }) =>
    stateManager.run(async () => {
      const response = await accountService.creditMemos.update({
        id,
        data,
        modifiedOn,
        toastMessageConfig: {
          success: { text: "Backcharge updated successfully." },
        },
      });
      return response;
    });

  const voidBackcharge = ({ id, modifiedOn }) =>
    stateManager.run(async () => {
      const response = await accountService.creditMemos.void({
        id,
        modifiedOn,
        toastMessageConfig: {
          success: { text: "Backcharge voided successfully." },
        },
      });
      return response;
    });

  return {
    isLoading,
    backcharges,
    deletePurchaseOrder,
    voidPurchaseOrder,
    createBackcharge,
    getBackcharges,
    updateBackcharge,
    voidBackcharge,
  };
};
